import {
  IconButton,
  DropdownMenu,
  EllipsisVerticalIcon,
} from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import { useTagsQuery } from '~publish/hooks/useTags'
import { ChannelFilterSubMenu } from '~publish/pages/AllChannels/ChannelFilterSubMenu'
import { TimezoneSubMenu } from '~publish/pages/AllChannels/TimezoneSubMenu'
import { TagFilterSubMenu } from '~publish/pages/Channel/TagFilterSubMenu'

import { TypeFilterSubMenu } from './TypeFilterSubMenu'

import type { JSX } from 'react'

export type CalendarActionsProps = Omit<ButtonProps, 'children'> & {
  channels?: MaskedFilterByChannelFragment
  selectedChannels?: string[]
  onChannelSelect?: (channelIds: string[]) => void
  disabledChannelIds?: Set<string>
  selectedTags?: string[]
  onTagSelect?: (tagIds: string[]) => void
  postingTimesItem?: JSX.Element
  triggerIcon?: React.ReactElement
}

type PostingTimesActionsProps = Omit<ButtonProps, 'children'> & {
  children: React.ReactNode
}

export function PostingTimesActions({
  children,
  ...buttonProps
}: PostingTimesActionsProps): JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton
          variant="tertiary"
          label="Posting times options"
          {...buttonProps}
        >
          <EllipsisVerticalIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>{children}</DropdownMenu.Content>
    </DropdownMenu>
  )
}

/**
 * Dropdown menu with filters and timezone for All-Channels Calendar view.
 */
export function CalendarActions({
  channels,
  selectedChannels = [],
  onChannelSelect,
  disabledChannelIds,
  selectedTags = [],
  onTagSelect,
  postingTimesItem,
  triggerIcon,
  ...buttonProps
}: CalendarActionsProps): JSX.Element {
  const { tags } = useTagsQuery()

  const showChannelFilter = channels && channels.length > 0 && onChannelSelect
  const showTagFilter = tags.length > 0 && onTagSelect

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton variant="tertiary" label="More actions" {...buttonProps}>
          {triggerIcon ?? <EllipsisVerticalIcon />}
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        {showChannelFilter && (
          <ChannelFilterSubMenu
            channels={channels}
            selectedChannelIds={selectedChannels}
            onChannelSelect={onChannelSelect}
            disabledChannelIds={disabledChannelIds}
          />
        )}

        <TypeFilterSubMenu />

        {showTagFilter && (
          <TagFilterSubMenu
            selectedTags={selectedTags}
            onTagSelect={onTagSelect}
          />
        )}

        <DropdownMenu.Separator />

        <TimezoneSubMenu />
        {postingTimesItem && (
          <>
            <DropdownMenu.Separator />
            {postingTimesItem}
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
